import { matchRoutes } from "react-router";

import { hasLiveSession } from "./routePrefetch";

// How long a prefetched link is left alone. Its queries carry their own
// staleTime, so this only stops repeated hovers from rerunning the loaders.
const PREFETCH_TTL_MS = 30_000;
const MAX_REDIRECTS = 2;

const recent = new Map();

const isRedirect = (result) =>
  result instanceof Response &&
  result.status >= 300 &&
  result.status < 400 &&
  result.headers.has("Location");

const saveData = () =>
  typeof navigator !== "undefined" && navigator.connection?.saveData === true;

// Loaded on first use: routes.js imports the app shell, which imports the
// components that call this.
const loadRoutes = () => import("./routes").then((m) => m.appRoutes);

const warm = async (path, redirectsLeft) => {
  const url = new URL(path, window.location.origin);
  const matches = matchRoutes(await loadRoutes(), url.pathname);
  if (!matches) {
    return;
  }
  const request = new Request(url.href);
  const run = (fn) => Promise.resolve().then(fn);
  const results = await Promise.allSettled(
    matches.flatMap(({ route, params }) => [
      typeof route.lazy === "function" ? run(() => route.lazy()) : null,
      typeof route.loader === "function"
        ? run(() => route.loader({ params, request, context: undefined }))
        : null,
    ]),
  );
  const redirect = results.find(
    (r) => r.status === "fulfilled" && isRedirect(r.value),
  );
  if (redirect && redirectsLeft > 0) {
    await warm(redirect.value.headers.get("Location"), redirectsLeft - 1);
  }
};

/**
 * Starts an in-app path's code chunks and first queries before the user clicks:
 * the matched routes' `lazy` and loaders run exactly as they would on
 * navigation, so the router and the page's useQuery pick up the work already
 * in flight. Best effort; nothing here throws.
 */
export const prefetchRoute = (path) => {
  if (!path || saveData() || !hasLiveSession()) {
    return;
  }
  const key = path.split("#")[0];
  const current = `${window.location.pathname}${window.location.search}`;
  const last = recent.get(key);
  if (key === current || (last && Date.now() - last < PREFETCH_TTL_MS)) {
    return;
  }
  recent.set(key, Date.now());
  warm(key, MAX_REDIRECTS).catch(() => {});
};

export const resetPrefetchedRoutes = () => recent.clear();
